<template>
  <section class="phd-license-pane">
    <header class="phd-license-head">
      <h3>License our content for your own LMS</h3>
      <p>Pick the courses you want to embed, tell us how many people will take them, and we'll handle the SCORM packaging and per-user licensing.</p>
    </header>

    <div class="phd-license-strip">
      <div class="phd-license-strip-item">
        <label class="phd-license-strip-label" for="phd-license-users">Employees who will take training</label>
        <input
          id="phd-license-users"
          type="number"
          min="1"
          max="100000"
          class="phd-license-strip-input"
          :value="users"
          @input="onNumber('users', $event.target.value)"
        />
      </div>
      <div class="phd-license-strip-item">
        <label class="phd-license-strip-label" for="phd-license-locs">Locations</label>
        <input
          id="phd-license-locs"
          type="number"
          min="1"
          max="10000"
          class="phd-license-strip-input"
          :value="locations"
          @input="onNumber('locations', $event.target.value)"
        />
      </div>
    </div>

    <div class="phd-license-courses">
      <div class="phd-license-courses-head">
        <h4>Pick your courses</h4>
        <span class="phd-license-courses-count">{{ selectedCourses.length }} selected</span>
      </div>
      <div class="phd-license-search">
        <span aria-hidden="true">🔎</span>
        <input
          type="search"
          v-model="query"
          class="phd-license-search-input"
          placeholder="Search the catalog…"
        />
      </div>

      <div v-if="!filteredCatalog.length" class="phd-license-empty">
        No courses match “{{ query }}”.
        <button v-if="query" type="button" class="phd-license-clear" @click="query = ''">Clear search</button>
      </div>

      <ul v-else class="phd-license-grid">
        <li
          v-for="entry in filteredCatalog"
          :key="entry.key"
          class="phd-license-course"
          :class="{
            'phd-license-course--on': !entry.isGroup && isSelected(entry.id),
            'phd-license-course--group': entry.isGroup,
            'phd-license-course--group-active': entry.isGroup && variantsSelected(entry) > 0,
          }"
        >
          <!-- Solo course — single checkbox row (existing behavior). -->
          <label v-if="!entry.isGroup" class="phd-license-course-label">
            <input
              type="checkbox"
              :checked="isSelected(entry.id)"
              @change="toggleCourse(entry)"
              class="phd-license-course-check"
            />
            <span class="phd-license-course-tick" aria-hidden="true">
              <span v-if="isSelected(entry.id)">✓</span>
            </span>
            <span class="phd-license-course-body">
              <span class="phd-license-course-name">{{ entry.name }}</span>
              <span class="phd-license-course-cat">{{ entry.category }}</span>
            </span>
            <span class="phd-license-course-price">${{ formatPrice(entry.cost) }}<small>/user</small></span>
          </label>

          <!-- Grouped course — button opens VariantPickerModal so the
               buyer can check the specific state/role versions they
               want to license. -->
          <button
            v-else
            type="button"
            class="phd-license-course-label phd-license-course-trigger"
            @click="openGroupPicker(entry)"
          >
            <span class="phd-license-course-tick" aria-hidden="true">
              <span v-if="variantsSelected(entry) > 0">{{ variantsSelected(entry) }}</span>
            </span>
            <span class="phd-license-course-body">
              <span class="phd-license-course-name">{{ entry.name }}</span>
              <span class="phd-license-course-cat">{{ entry.category }}</span>
            </span>
            <span class="phd-license-course-price">
              <template v-if="!entry.costSame">From </template>${{ formatPrice(entry.cost) }}<small>/user</small>
            </span>
          </button>
        </li>
      </ul>
    </div>

    <!-- Variant picker, license-mode: selecting toggles licenseConfig
         .selectedCourseIds rather than adding to cart. -->
    <VariantPickerModal
      :open="pickerOpen"
      :group="pickerGroup"
      :cart-ids="(value && value.selectedCourseIds) || []"
      :busy-id="null"
      @pick="onVariantPicked"
      @close="closeGroupPicker"
    />
  </section>
</template>

<script>
import { groupCourses } from '../../utils/courseGrouping';
import VariantPickerModal from './VariantPickerModal.vue';

const LOCATION_FEE = 19;

export default {
  name: 'LicensePane',
  components: { VariantPickerModal },

  props: {
    catalog: { type: Array, default: () => [] },
    // v-model'd by parent so all the license-mode state round-trips
    // through PricingPage.vue's persistUiState the same way companyConfig
    // does. Shape: { users, locations, selectedCourseIds: number[] }.
    value: {
      type: Object,
      default: () => ({ users: 25, locations: 1, selectedCourseIds: [] }),
    },
  },

  data() {
    return {
      query: '',
      pickerOpen: false,
      pickerGroup: null,
    };
  },

  computed: {
    users() { return Math.max(1, Number((this.value || {}).users) || 1); },
    locations() { return Math.max(1, Number((this.value || {}).locations) || 1); },
    selectedCourses() {
      const ids = ((this.value || {}).selectedCourseIds) || [];
      return (this.catalog || []).filter((c) => ids.indexOf(c.id) !== -1);
    },
    filteredCatalog() {
      // Search-then-group so a query that matches one variant of a group
      // still surfaces the whole group as a single row (less confusing
      // than a partial group).
      const q = String(this.query || '').trim().toLowerCase();
      let pool = this.catalog || [];
      if (q) {
        pool = pool.filter((c) =>
          (c.name || '').toLowerCase().includes(q)
          || (c.category || '').toLowerCase().includes(q));
      }
      return groupCourses(pool);
    },
    courseSubtotal() {
      // Per-user × per-course cost. Mirrors the legacy SelectCourses
      // behavior where each licensed course bills per learner who takes
      // it. We use the same `cost` field the catalog already exposes.
      return this.selectedCourses.reduce((sum, c) => sum + (Number(c.cost) || 0), 0) * this.users;
    },
    locationSubtotal() {
      return this.locations * LOCATION_FEE;
    },
    monthlyEstimate() {
      return this.courseSubtotal + this.locationSubtotal;
    },
    annualEstimate() {
      // 10% annual discount mirroring the LMS plans' annual cycle savings.
      return this.monthlyEstimate * 12 * 0.9;
    },
  },

  watch: {
    // When parent toggles into license mode for the first time, it may
    // pass an empty value — re-emit the defaults so persistUiState picks
    // them up.
    value: {
      immediate: true,
      handler(val) {
        if (!val || typeof val !== 'object') {
          this.$emit('input', { users: 25, locations: 1, selectedCourseIds: [] });
        }
      },
    },
  },

  methods: {
    formatPrice(n) {
      const num = Number(n);
      if (!Number.isFinite(num)) return '0.00';
      return num.toFixed(2);
    },
    isSelected(id) {
      const ids = ((this.value || {}).selectedCourseIds) || [];
      return ids.indexOf(id) !== -1;
    },
    onNumber(key, raw) {
      const v = parseInt(raw, 10);
      const safe = Number.isFinite(v) && v > 0 ? v : 1;
      this.$emit('input', { ...(this.value || {}), [key]: safe });
      this.$emit('estimate-changed', this.annualEstimate);
    },
    toggleCourse(course) {
      const ids = (((this.value || {}).selectedCourseIds) || []).slice();
      const idx = ids.indexOf(course.id);
      if (idx === -1) ids.push(course.id);
      else ids.splice(idx, 1);
      this.$emit('input', { ...(this.value || {}), selectedCourseIds: ids });
      this.$emit('estimate-changed', this.annualEstimate);
    },

    /**
     * Count how many of a group's variants are already in the buyer's
     * selectedCourseIds. Drives the "X" overlay on the grouped row's
     * checkbox tile + the active class.
     */
    variantsSelected(group) {
      if (!group || !group.isGroup || !Array.isArray(group.variants)) return 0;
      const ids = new Set(((this.value || {}).selectedCourseIds) || []);
      return group.variants.reduce((n, v) => n + (ids.has(v.id) ? 1 : 0), 0);
    },

    openGroupPicker(group) {
      this.pickerGroup = group;
      this.pickerOpen = true;
    },

    closeGroupPicker() {
      this.pickerOpen = false;
      this.pickerGroup = null;
    },

    /**
     * VariantPickerModal emits 'pick' with a course id when the buyer
     * clicks Add/Remove on a variant row. License-mode just toggles
     * selectedCourseIds — no cart round-trip.
     */
    onVariantPicked(id) {
      this.toggleCourse({ id });
    },
  },
};
</script>

<style lang="scss" scoped>
.phd-license-pane {
  margin-top: 8px;
}

.phd-license-head {
  margin-bottom: 16px;
  h3 {
    font-size: 22px;
    line-height: 28px;
    font-weight: 800;
    color: #1a1a1a;
    margin: 0 0 6px;
  }
  p {
    font-size: 14px;
    line-height: 20px;
    color: #555;
    margin: 0;
  }
}

.phd-license-strip {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 18px;
}

.phd-license-strip-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.phd-license-strip-label {
  font-size: 12px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.phd-license-strip-input {
  height: 44px;
  border: 1.5px solid rgba(37, 37, 37, 0.15);
  background: #fff;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  outline: none;
  padding: 0 14px;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  font-family: inherit;
}
.phd-license-strip-input:hover {
  border-color: rgba(254, 103, 44, 0.5);
}
.phd-license-strip-input:focus {
  border-color: #fe672c;
  box-shadow: 0 0 0 3px rgba(254, 103, 44, 0.18);
}

.phd-license-courses {
  background: #fff;
  border: 1px solid rgba(37, 37, 37, 0.1);
  border-radius: 12px;
  padding: 14px;
}

.phd-license-courses-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  h4 {
    font-size: 15px;
    line-height: 19px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
  }
  .phd-license-courses-count {
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 12px;
    background: rgba(254, 103, 44, 0.12);
    color: #d34c14;
  }
}

.phd-license-search {
  position: relative;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: #f7f7f7;
  border-radius: 10px;

  .phd-license-search-input {
    flex: 1;
    border: 0;
    background: transparent;
    height: 40px;
    font-size: 14px;
    color: #1a1a1a;
    outline: none;
  }
}

.phd-license-empty {
  text-align: center;
  padding: 24px 12px;
  color: #777;
  font-size: 14px;

  .phd-license-clear {
    display: inline-block;
    margin-left: 6px;
    background: transparent;
    color: #fe672c;
    border: 0;
    font-weight: 700;
    cursor: pointer;
  }
}

.phd-license-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
}

.phd-license-course {
  border-radius: 10px;
  border: 1px solid rgba(37, 37, 37, 0.08);
  background: #fff;
  transition: border-color 180ms ease, background-color 180ms ease;

  &--on {
    border-color: #fe672c;
    background: linear-gradient(135deg, #fff8f4 0%, #fff 100%);
  }
}

.phd-license-course-label {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  margin: 0;
}

.phd-license-course-check {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.phd-license-course-tick {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 2px solid rgba(200, 200, 200, 0.7);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  font-weight: 800;
  font-size: 12px;
  color: #fff;

  .phd-license-course--on & {
    background: #fe672c;
    border-color: #fe672c;
  }
}

.phd-license-course-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.phd-license-course-name {
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.phd-license-course-cat {
  font-size: 12px;
  line-height: 16px;
  color: #888;
  margin-top: 1px;
}

.phd-license-course-price {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  small {
    font-size: 11px;
    font-weight: 500;
    color: #777;
  }
}

@media (min-width: 768px) {
  .phd-license-strip {
    grid-template-columns: 1fr 1fr 1fr;
  }
  .phd-license-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .phd-license-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
